import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useWakeLock }      from './useWakeLock'
import { AmbientDisplay }   from './AmbientDisplay'
import { DisplaySetupPage } from './DisplaySetupPage'
import { getDisplayData }   from '../../lib/displayApi'

const TOKEN_KEY = 'doorchart_display_token'
const POLL_MS   = 30 * 1000

export default function DisplayPage() {
  const { code } = useParams() // set when routed via /d/:code

  const [token,       setToken]       = useState(null)
  const [displayData, setDisplayData] = useState(null)
  const [loading,     setLoading]     = useState(true)
  const [invalid,     setInvalid]     = useState(false)
  const [isOnline,    setIsOnline]    = useState(navigator.onLine)
  const pollRef = useRef(null)

  useWakeLock()

  // Keep online state accurate
  useEffect(() => {
    const online  = () => setIsOnline(true)
    const offline = () => setIsOnline(false)
    window.addEventListener('online',  online)
    window.addEventListener('offline', offline)
    return () => { window.removeEventListener('online', online); window.removeEventListener('offline', offline) }
  }, [])

  // Dark body background while on this route
  useEffect(() => {
    const prev = document.body.style.backgroundColor
    document.body.style.backgroundColor = '#0d0d1a'
    return () => { document.body.style.backgroundColor = prev }
  }, [])

  const load = useCallback(async (tok) => {
    try {
      const data = await getDisplayData(tok)
      setDisplayData(data)
      setInvalid(false)
    } catch (_) {
      if (!displayData) setInvalid(true)
      // else: keep showing stale data (offline)
    }
  }, [displayData])

  useEffect(() => {
    // Priority: /d/:code route param → ?token= query string → localStorage
    let tok = code ?? null

    if (!tok) {
      const params = new URLSearchParams(window.location.search)
      tok = params.get('token')
      if (tok) {
        // Clean legacy ?token= from URL bar
        const url = new URL(window.location.href)
        url.searchParams.delete('token')
        window.history.replaceState({}, '', url.toString())
      }
    }

    if (tok) {
      localStorage.setItem(TOKEN_KEY, tok)
    } else {
      tok = localStorage.getItem(TOKEN_KEY)
    }

    if (!tok) {
      setLoading(false)
      setInvalid(true)
      return
    }

    setToken(tok)
    load(tok).finally(() => setLoading(false))
  }, [code]) // eslint-disable-line react-hooks/exhaustive-deps

  // Polling every 30s
  useEffect(() => {
    if (!token) return
    pollRef.current = setInterval(() => load(token), POLL_MS)
    return () => clearInterval(pollRef.current)
  }, [token, load])

  // Re-fetch on reconnect
  useEffect(() => {
    if (isOnline && token && displayData) load(token)
  }, [isOnline]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#0d0d1a] flex items-center justify-center">
        <div className="text-4xl animate-pulse">🏠</div>
      </div>
    )
  }

  if (invalid || !displayData) {
    return <DisplaySetupPage />
  }

  return (
    <AmbientDisplay
      displayData={displayData}
      deviceToken={token}
      isOnline={isOnline}
    />
  )
}
